import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, intToHex } from '../utils/constants';
import { eventBus } from '../systems/EventBus';
import { HeadTracker } from '../systems/HeadTracker';

export default class CalibrationScene extends Phaser.Scene {
  private readonly FEED_X = GAME_W / 2;
  private readonly FEED_Y = GAME_H / 2 + 30;
  private readonly FEED_W = 320;
  private readonly FEED_H = 200;

  private crosshair!: Phaser.GameObjects.Graphics;
  private progress!: Phaser.GameObjects.Graphics;
  private countdownText!: Phaser.GameObjects.Text;
  private calProgress = 0;
  private calTimer: Phaser.Time.TimerEvent | null = null;
  private headTracker: HeadTracker | null = null;
  private keyboardOnly = false;
  private useCamera = false;
  private useKeyboard = true;
  private playerName = '';

  constructor() {
    super({ key: 'CalibrationScene' });
  }

  init(data: { headTracker?: HeadTracker | null; keyboardOnly?: boolean; playerName?: string; useCamera?: boolean; useKeyboard?: boolean }) {
    this.headTracker = data.headTracker || null;
    this.keyboardOnly = data.keyboardOnly ?? true;
    this.playerName = data.playerName || '';
    this.useCamera = data.useCamera ?? !!this.headTracker;
    this.useKeyboard = data.useKeyboard ?? true;
  }

  create() {
    this.cameras.main.setBackgroundColor(COLORS.bgDark);

    this.add.text(GAME_W / 2, 80, '头部校准', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '32px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(0.5);

    if (this.useKeyboard) {
      this.add.text(GAME_W / 2, 125, '键盘已启用  |  方向键移动  J攻击 K重击 L技能', {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: '#555577',
      }).setOrigin(0.5);
    }

    this.add.text(GAME_W / 2, this.useKeyboard ? 150 : 130, '保持头部自然居中，不要移动', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '16px', color: intToHex(COLORS.gray),
    }).setOrigin(0.5);

    const feedX = this.FEED_X;
    const feedY = this.FEED_Y;
    const feedW = this.FEED_W;
    const feedH = this.FEED_H;

    const feedFrame = this.add.graphics();
    feedFrame.fillStyle(0x000000, 0.4);
    feedFrame.fillRoundedRect(feedX - feedW / 2 - 2, feedY - feedH / 2 - 2, feedW + 4, feedH + 4, 10);
    feedFrame.lineStyle(2, COLORS.cyan, 0.5);
    feedFrame.strokeRoundedRect(feedX - feedW / 2 - 2, feedY - feedH / 2 - 2, feedW + 4, feedH + 4, 10);

    this.add.text(feedX, feedY - feedH / 2 - 20, '摄像头画面', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#555577',
    }).setOrigin(0.5);

    if (this.headTracker) {
      const video = this.headTracker.getVideoElement();
      if (video) {
        video.style.display = 'block';
        video.style.position = 'absolute';
        video.style.objectFit = 'cover';

        const reposition = () => {
          const r = this.game.canvas.getBoundingClientRect();
          const sx = r.width / GAME_W;
          const sy = r.height / GAME_H;
          video.style.left = (r.left + (feedX - feedW / 2) * sx) + 'px';
          video.style.top = (r.top + (feedY - feedH / 2) * sy) + 'px';
          video.style.width = (feedW * sx) + 'px';
          video.style.height = (feedH * sy) + 'px';
          video.style.borderRadius = (8 * Math.min(sx, sy)) + 'px';
          video.style.border = (2 * Math.min(sx, sy)) + 'px solid #00f0ff';
          video.style.opacity = '0.7';
          video.style.zIndex = '1000';
        };
        reposition();
        window.addEventListener('resize', reposition);
        this.events.on('shutdown', () => {
          window.removeEventListener('resize', reposition);
          video.style.display = 'none';
        });
      }
    }

    this.crosshair = this.add.graphics();
    this.crosshair.setPosition(feedX, feedY);
    this.drawCrosshair();

    this.progress = this.add.graphics();

    this.countdownText = this.add.text(GAME_W / 2, feedY + feedH / 2 + 40, '', {
      fontFamily: 'Arial', fontSize: '28px', color: intToHex(COLORS.white), fontStyle: 'bold',
    }).setOrigin(0.5);

    this.time.delayedCall(2000, () => { this.startCalibration(); });

    const skipBtn = this.add.text(GAME_W - 30, GAME_H - 30, '跳过 >>', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '16px', color: intToHex(COLORS.gray),
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    skipBtn.on('pointerdown', () => this.goToBattle());
  }

  private drawCrosshair() {
    this.crosshair.clear();
    const alpha = 0.5 + Math.sin(this.time.now * 0.003) * 0.3;
    this.crosshair.lineStyle(2, COLORS.cyan, alpha);
    this.crosshair.strokeCircle(0, 0, 30);
    this.crosshair.lineStyle(1, COLORS.cyan, alpha * 0.5);
    this.crosshair.lineBetween(-40, 0, -20, 0);
    this.crosshair.lineBetween(20, 0, 40, 0);
    this.crosshair.lineBetween(0, -40, 0, -20);
    this.crosshair.lineBetween(0, 20, 0, 40);
  }

  private startCalibration() {
    let countdown = 3;
    this.countdownText.setText(countdown.toString());

    this.calTimer = this.time.addEvent({
      delay: 1000,
      callback: () => {
        countdown--;
        if (countdown > 0) {
          this.countdownText.setText(countdown.toString());
        } else {
          this.countdownText.setText('校准中...');
          this.calibrate();
        }
      },
      loop: true,
    });
  }

  private calibrate() {
    const duration = 2000;
    const interval = 50;
    this.time.addEvent({
      delay: interval,
      callback: () => {
        this.calProgress += interval / duration;
        this.drawProgressBar();
        if (this.calProgress >= 1) {
          this.time.removeAllEvents();
          this.goToBattle();
        }
      },
      loop: true,
    });
  }

  private drawProgressBar() {
    this.progress.clear();
    const x = GAME_W / 2 - 150;
    const y = this.FEED_Y + this.FEED_H / 2 + 10;
    const w = 300, h = 8;

    this.progress.fillStyle(COLORS.darkGray, 1);
    this.progress.fillRect(x, y, w, h);
    this.progress.fillStyle(COLORS.cyan, 1);
    this.progress.fillRect(x, y, w * Math.min(this.calProgress, 1), h);
    this.progress.lineStyle(1, COLORS.cyan, 0.5);
    this.progress.strokeRect(x, y, w, h);
  }

  private goToBattle() {
    if (this.calTimer) this.calTimer.remove();
    if (this.headTracker) {
      const video = this.headTracker.getVideoElement();
      if (video) video.style.display = 'none';
    }
    eventBus.emit('calibration-complete');
    this.cameras.main.fadeOut(500, 0, 0, 30);
    this.time.delayedCall(500, () => {
      this.scene.start('BattleScene', {
        headTracker: this.headTracker,
        keyboardOnly: this.keyboardOnly,
        playerName: this.playerName,
        useCamera: this.useCamera,
        useKeyboard: this.useKeyboard,
      });
    });
  }
}
